import {
    IconChevronDown, IconChevronRight, IconDotsVertical, IconGripVertical,
    IconPencil, IconPlus, IconTrash,
} from '@tabler/icons-react';
import { useState } from 'react';
import {
    Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Progress,
} from 'reactstrap';
import { CSS } from '@dnd-kit/utilities';
import { useSortable } from '@dnd-kit/sortable';
import { NSBadge, NSButton, NSEditableText } from 'bricks';
import NSIcon from 'bricks/NSIcon/NSIcon';
import handleIconKeyDown from 'helpers/handleIconKeyDown';
import { TimelineSection } from 'views/Tasks/Timeline/types';
import './TimelineSectionRow.scss';

type TimelineSectionRowProps = {
    section: TimelineSection;
    completedCount: number;
    totalCount: number;
    isExpanded: boolean;
    onToggleExpand: () => void;
    onRename: (name: string) => void;
    onAddActivity: () => void;
    onRequestDelete: () => void;
    children?: React.ReactNode;
};

const TimelineSectionRow = ({
    section, completedCount, totalCount, isExpanded, onToggleExpand, onRename, onAddActivity, onRequestDelete, children,
}: TimelineSectionRowProps) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isEditingName, setIsEditingName] = useState(false);

    const {
        attributes, listeners, setNodeRef, transform, transition, isDragging,
    } = useSortable({ id: section.id });

    const progressValue = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

    const startEditingName = (e: React.MouseEvent | React.KeyboardEvent) => {
        e.stopPropagation();
        setIsEditingName(true);
    };

    return (
        <tbody
            ref={setNodeRef}
            className="TimelineSectionGroup"
            style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 }}
            data-testid={`timeline-section-${section.id}`}
        >
            <tr className="TimelineSectionRow">
                <td colSpan={7}>
                    <div className="d-flex align-items-center">
                        <span
                            {...attributes}
                            {...listeners}
                            aria-label="Drag to reorder section"
                            className="TimelineSectionRow__drag-handle d-inline-flex align-items-center justify-content-center cursor--grab mr-1"
                            data-testid={`timeline-section-${section.id}-drag-handle`}
                        >
                            <NSIcon icon={IconGripVertical} className="text-muted" />
                        </span>
                        <NSIcon
                            icon={isExpanded ? IconChevronDown : IconChevronRight}
                            size="xs"
                            className="TimelineSectionRow__toggle cursor--pointer mr-1"
                            onClick={onToggleExpand}
                            onKeyDown={handleIconKeyDown(() => onToggleExpand())}
                            aria-label={isExpanded ? 'Collapse section' : 'Expand section'}
                            aria-hidden={false}
                            focusable
                            data-testid={`timeline-section-${section.id}-toggle`}
                        />
                        <div className="d-flex align-items-center gap-2">
                            <NSEditableText
                                value={section.name}
                                onSave={onRename}
                                name={`timeline-section-name-${section.id}`}
                                textClassName="TimelineSectionRow__name text-nowrap font-weight-bold"
                                dataTestIdPrefix={`timeline-section-${section.id}-name`}
                                iconSize="sm"
                                showEditIcon={false}
                                isEditing={isEditingName}
                                onEditingChange={setIsEditingName}
                            />

                            {!isEditingName && (
                                <>
                                    <NSButton
                                        icon={IconPencil}
                                        size="sm"
                                        className="text-muted my-auto ml-2"
                                        callback={startEditingName}
                                        ariaLabel="Save comment"
                                        color='light'
                                        onMouseDown={(e: React.MouseEvent) => e.preventDefault()}
                                        dataTestId={`timelineSection-${section.id}-editActivity`}
                                    />
                                    <NSButton
                                        icon={IconPlus}
                                        size="sm"
                                        className="text-muted my-auto ml-2"
                                        callback={onAddActivity}
                                        ariaLabel="Add activity to section"
                                        color='light'
                                        onMouseDown={(e: React.MouseEvent) => e.preventDefault()}
                                        dataTestId={`timelineSection-${section.id}-addActivity`}
                                    />
                                </>
                            )}
                        </div>
                        <div className='ml-auto d-flex'>
                            <NSBadge className="ml-2" color="dark-lighten">
                                {`${completedCount} of ${totalCount} complete`}
                            </NSBadge>
                            <div className="TimelineSectionRow__progress my-auto ml-2">
                                <Progress
                                    color="success"
                                    value={progressValue}
                                    data-testid={`timeline-section-${section.id}-progress`}
                                />
                            </div>
                        </div>
                    </div>
                </td>
                <td onClick={e => e.stopPropagation()}>
                    <Dropdown isOpen={isMenuOpen} toggle={() => setIsMenuOpen(prev => !prev)} direction="down">
                        <DropdownToggle tag="button" className="btn p-0 border-0 bg-transparent">
                            <NSButton
                                color="secondary"
                                size="sm"
                                icon={IconDotsVertical}
                                ariaLabel="Section actions"
                                className="TimelineSectionRow__menu-toggle"
                            />
                        </DropdownToggle>
                        <DropdownMenu end>
                            <DropdownItem
                                onClick={onRequestDelete}
                                className="text-white"
                                data-testid={`timeline-section-${section.id}-delete`}
                            >
                                <div className="d-flex align-items-center justify-content-between w-100">
                                    <span>Delete section</span>
                                    <NSIcon icon={IconTrash} />
                                </div>
                            </DropdownItem>
                        </DropdownMenu>
                    </Dropdown>
                </td>
            </tr>
            {isExpanded && children}
        </tbody>
    );
};

export default TimelineSectionRow;
